import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { FabFiltersBar } from '@/components/product-insights/FabFiltersBar';
import { LayoutToolbar } from '@/components/layout';
import { FAB_OVERVIEW_LAYOUT, FabOverview } from '@/components/product-insights/FabOverview';
import { FabScholarshipsTab } from '@/components/product-insights/FabScholarshipsTab';
import { FabSchoolLinksPanel } from '@/components/product-insights/FabSchoolLinksPanel';
import { FabSchoolsTab } from '@/components/product-insights/FabSchoolsTab';
import { UpdatedFact } from '@/components/product-insights/fab-ui';
import {
  DEFAULT_FAB_STAGE,
  applyFilterState,
  filterStatesEqual,
  readFilterState,
  writeFilterState,
  type FabFilterState,
  type FabStage,
} from '@/components/product-insights/fab-filter-state';
import {
  PiDeniedPage,
  PiError,
  PiPageLayout,
  PiTabs,
  PiToolbar,
} from '@/components/product-insights/pi-kit';
import { useFabFilters, useFabOverview } from '@/components/product-insights/use-fab-data';
import { Select } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { formatDateOnly } from '@/lib/format';
import { useAuth } from '@/lib/use-auth';
import { usePageLayout } from '@/lib/use-page-layout';

/**
 * /product-insights/fab — SEED FAB, and /product-insights/scholarships —
 * Scholarships (pi-kit template, owner 2026-10-19).
 *
 * Live read of SEED FAB's funnel through EMERGE's `/product-insights/fab/*`
 * proxy (15-minute server cache, nothing stored). SEED FAB has three tabs,
 * `tab=` in the URL (absent = overview): Overview · Schools · School links.
 * Scholarships is its own Product Insights section (owner 2026-10-19) over the
 * same data and the same filter keys; an old `/product-insights/fab?tab=scholarships`
 * link redirects there with its filters.
 *
 * - One data set (`useFabOverview`) feeds Overview, Schools and Scholarships,
 *   so a tab switch never refetches. Search (`q`) and the Schools stage toggle
 *   (`stage`) are client-side views stripped from the fetch state.
 * - Filters are shared by every tab and live in one toolbar (Cycle + the
 *   FabFiltersBar, which owns its chips and "Clear all"). A tab switch clears
 *   only `q`, the per-tab search.
 * - The cycle is SEED FAB's own (1 Jun – 31 May), never an EMERGE cycle; its
 *   date range is the facts line, a fact about the data.
 * - No headline tiles: the Overview funnel is the headline, and tiles would
 *   show the same figures twice.
 * - Layout manager (2026-10-20): only the Overview tab has movable panels
 *   (`product-insights.fab.overview`); its LayoutToolbar shows on that tab
 *   only. Schools, School links and Scholarships are one panel each.
 */

type FabTab = 'overview' | 'schools' | 'scholarships' | 'links';

type FabSection = 'fab' | 'scholarships';

const SCHOLARSHIPS_PATH = '/product-insights/scholarships';
const TAB_PARAM = 'tab';
const SEARCH_PARAM = 'q';
const SEARCH_DEBOUNCE_MS = 250;

function parseTab(raw: string | null): FabTab {
  return raw === 'schools' || raw === 'scholarships' || raw === 'links' ? raw : 'overview';
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message || fallback;
  return fallback;
}

function useUnlinkedCount(enabled: boolean, cycleId: number | null, refreshKey: string): number | null {
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const { signal } = controller;
    api
      .listFabSchoolLinks(cycleId ?? undefined, signal)
      .then((rows) => {
        if (signal.aborted) return;
        setCount(rows.filter((row) => row.company === null).length);
      })
      .catch(() => {
        // The count is a convenience; the School links tab reports its own errors.
        if (signal.aborted) return;
        setCount(null);
      });
    return () => controller.abort();
  }, [enabled, cycleId, refreshKey]);
  return enabled ? count : null;
}

const SECTION_TITLE: Record<FabSection, string> = { fab: 'Funnel Analytics & BI', scholarships: 'Scholarships' };

export function ProductInsightsFabPage() {
  return <FabSectionPage section="fab" />;
}

export function ProductInsightsScholarshipsPage() {
  return <FabSectionPage section="scholarships" />;
}

function FabSectionPage({ section }: { section: FabSection }) {
  const { ability } = useAuth();
  // Without read nothing is fetched: the refusal renders before the data
  // component (and its hooks) mount.
  if (!ability.canRead('product-insights')) {
    return <PiDeniedPage product={section} title={SECTION_TITLE[section]} />;
  }
  return <FabContent section={section} canEdit={ability.canUpdate('product-insights')} />;
}

/** The URL's query without `tab`, for moving between the two sections. */
function withoutTab(params: URLSearchParams): string {
  const next = new URLSearchParams(params);
  next.delete(TAB_PARAM);
  const query = next.toString();
  return query ? `?${query}` : '';
}

function FabContent({ section, canEdit }: { section: FabSection; canEdit: boolean }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlTab = parseTab(searchParams.get(TAB_PARAM));
  const tab: FabTab = section === 'scholarships' ? 'scholarships' : urlTab;
  const redirectToScholarships = section === 'fab' && urlTab === 'scholarships';
  const urlState = useMemo(() => readFilterState(searchParams), [searchParams]);

  /* --- debounced search ------------------------------------------------ */

  const [draftSearch, setDraftSearch] = useState(urlState.search);
  const [seenUrlSearch, setSeenUrlSearch] = useState(urlState.search);
  if (seenUrlSearch !== urlState.search) {
    // The URL moved on its own (Back, a tab switch, a drill-in): follow it.
    setSeenUrlSearch(urlState.search);
    setDraftSearch(urlState.search);
  }

  useEffect(() => {
    if (draftSearch === urlState.search) return;
    const timer = window.setTimeout(() => {
      setSearchParams(
        (prev) => applyFilterState(prev, { ...readFilterState(prev), search: draftSearch }),
        { replace: true },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draftSearch, urlState.search, setSearchParams]);

  const viewState = useMemo<FabFilterState>(
    () => ({ ...urlState, search: draftSearch }),
    [urlState, draftSearch],
  );

  const onStateChange = useCallback(
    (next: FabFilterState) => {
      setDraftSearch(next.search);
      const onlySearchChanged = filterStatesEqual(
        { ...next, search: '' },
        { ...viewState, search: '' },
      );
      if (onlySearchChanged) return;
      writeFilterState(next, setSearchParams);
    },
    [viewState, setSearchParams],
  );

  /* --- tabs + drill-ins ------------------------------------------------- */

  const goToTab = useCallback(
    (next: FabTab, patch?: { search?: string; stage?: FabStage }) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete(SEARCH_PARAM);
          if (next === 'overview') params.delete(TAB_PARAM);
          else params.set(TAB_PARAM, next);
          if (patch) {
            const current = readFilterState(params);
            return applyFilterState(params, {
              ...current,
              search: patch.search ?? current.search,
              stage: patch.stage ?? current.stage,
            });
          }
          return params;
        },
        { replace: !patch },
      );
    },
    [setSearchParams],
  );

  const changeTab = useCallback((next: FabTab) => goToTab(next), [goToTab]);
  const onOpenSchools = useCallback(
    (stage: FabStage) => goToTab('schools', { stage, search: '' }),
    [goToTab],
  );
  const onOpenScholarships = useCallback(
    (search?: string) => {
      const params = new URLSearchParams(searchParams);
      if (search) params.set(SEARCH_PARAM, search);
      else params.delete(SEARCH_PARAM);
      navigate(`${SCHOLARSHIPS_PATH}${withoutTab(params)}`);
    },
    [navigate, searchParams],
  );
  const onOpenSchool = useCallback(
    (name: string) => goToTab('schools', { search: name, stage: DEFAULT_FAB_STAGE }),
    [goToTab],
  );

  /* --- data ------------------------------------------------------------- */

  const cycleId = urlState.cycleId;
  const filtersResult = useFabFilters(cycleId);
  const { filters, countries } = filtersResult;
  const reloadFilters = filtersResult.reload;

  // Search and the stage toggle are client-side views over one fetched set.
  // Keyed by value so a search keystroke keeps the same object identity.
  const fetchKey = JSON.stringify({ ...urlState, search: '', stage: DEFAULT_FAB_STAGE });
  const fetchState = useMemo(() => JSON.parse(fetchKey) as FabFilterState, [fetchKey]);
  const [reloadToken, setReloadToken] = useState(0);
  const overview = useFabOverview(fetchState, reloadToken);

  const refresh = useCallback(() => setReloadToken((n) => n + 1), []);
  const retry = useCallback(() => {
    reloadFilters();
    setReloadToken((n) => n + 1);
  }, [reloadFilters]);

  const overviewLayout = usePageLayout('product-insights.fab.overview', FAB_OVERVIEW_LAYOUT);

  const unlinkedCount = useUnlinkedCount(canEdit && section === 'fab', cycleId, `${tab}:${reloadToken}`);

  const selectedCycleId = cycleId ?? filters?.resolvedCycleId ?? null;
  const selectedCycle = useMemo(
    () => filters?.cycles.find((c) => c.id === selectedCycleId) ?? null,
    [filters, selectedCycleId],
  );

  const onCycleChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const id = Number.parseInt(event.target.value, 10);
      if (!Number.isSafeInteger(id)) return;
      // The resolved (current) cycle is the default: an absent key.
      const nextCycleId = filters && id === filters.resolvedCycleId ? null : id;
      writeFilterState({ ...urlState, cycleId: nextCycleId }, setSearchParams);
    },
    [filters, urlState, setSearchParams],
  );

  const data = overview.data;
  const tabs = useMemo(
    () =>
      [
        { key: 'overview', label: 'Overview' },
        { key: 'schools', label: 'Schools', count: data ? data.merged.length : null },
        // Unlinked schools (the to-do count), only when there is something to link.
        {
          key: 'links',
          label: 'School links',
          count: unlinkedCount !== null && unlinkedCount > 0 ? unlinkedCount : null,
        },
      ] as const satisfies readonly { key: FabTab; label: string; count?: number | null }[],
    [data, unlinkedCount],
  );

  // Each tab renders its own load error; only the filters (cycle list) failure is page-wide.
  const pageError = filtersResult.error;

  const facts = selectedCycle ? (
    <span>
      {formatDateOnly(selectedCycle.startDate)} – {formatDateOnly(selectedCycle.endDate)}
    </span>
  ) : undefined;

  const actions =
    tab !== 'links' ? (
      <>
        <UpdatedFact
          fetchedAt={data?.fetchedAt ?? null}
          refreshing={overview.refreshing || (overview.loading && data !== null)}
          onRefresh={refresh}
        />
        {tab === 'overview' && <LayoutToolbar layout={overviewLayout} />}
      </>
    ) : undefined;

  const cycleControl = (
    <div className="w-48 max-sm:w-full">
      <Select
        aria-label="Cycle"
        value={selectedCycleId !== null ? String(selectedCycleId) : ''}
        onChange={onCycleChange}
        disabled={!filters}
      >
        {!filters && <option value="">Cycle</option>}
        {filters?.cycles.map((c) => (
          <option key={c.id} value={String(c.id)}>
            {c.isCurrent ? `${c.name} (current)` : c.name}
          </option>
        ))}
      </Select>
    </div>
  );

  if (redirectToScholarships) {
    return <Navigate to={`${SCHOLARSHIPS_PATH}${withoutTab(searchParams)}`} replace />;
  }

  return (
    <PiPageLayout product={section} title={SECTION_TITLE[section]} facts={facts} actions={actions}>
      <PiToolbar>
        {tab === 'links' && cycleControl}
        {tab !== 'links' && (
          <div className="min-w-0 flex-1 max-sm:basis-full">
            <FabFiltersBar
              filters={filters}
              countries={countries}
              value={viewState}
              onChange={onStateChange}
              showSearch={tab === 'schools' || tab === 'scholarships'}
              showScholarshipFilters={tab === 'overview' || tab === 'scholarships'}
            />
          </div>
        )}
      </PiToolbar>

      {section === 'fab' && (
        <PiTabs tabs={tabs} value={tab} onChange={changeTab} ariaLabel="Funnel Analytics & BI section" />
      )}

      {pageError !== null && pageError !== undefined && (
        <PiError message={errorMessage(pageError, 'Funnel Analytics & BI could not be reached.')} onRetry={retry} />
      )}

      {tab === 'overview' && (
        <FabOverview
          layout={overviewLayout}
          data={data}
          loading={overview.loading}
          error={overview.error}
          filters={filters}
          onOpenSchools={onOpenSchools}
          onOpenScholarships={onOpenScholarships}
          onOpenSchool={onOpenSchool}
        />
      )}
      {tab === 'schools' && (
        <FabSchoolsTab
          data={data}
          loading={overview.loading}
          error={overview.error}
          state={viewState}
          onStateChange={onStateChange}
          filters={filters}
        />
      )}
      {tab === 'scholarships' && (
        <FabScholarshipsTab
          data={data}
          loading={overview.loading}
          error={overview.error}
          state={viewState}
          onStateChange={onStateChange}
          filters={filters}
        />
      )}
      {tab === 'links' && <FabSchoolLinksPanel cycleId={cycleId} canEdit={canEdit} />}
    </PiPageLayout>
  );
}

export default ProductInsightsFabPage;
